import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import toast from 'react-hot-toast';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { cn } from '../lib/utils';

const {
  LuStar, LuMessageSquare, LuClipboardList, LuSmile, LuSave,
  LuCopy, LuClipboard, LuRefreshCw,
} = LuIcons;

const CLIPBOARD_KEY = 'portfolio_notes_clipboard';

const HOMEWORK_STATUSES = [
  'No Homework',
  'Completed',
  'Not Attempted',
  'Good Attempt',
  'Half Done',
  'Just Started',
];

const EMPTY_FORM = {
  notes: '',
  homework: '',
  behavior_notes: '',
  score: 3,
  homework_status: 'No Homework',
};

export default function PortfolioModal({ isOpen, onClose, attendanceRecord, onSave }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [clipboardData, setClipboardData] = useState(null);

  // Always fetch fresh data from Supabase when modal opens or on manual refresh
  const fetchPortfolio = useCallback(async () => {
    if (!attendanceRecord?.id) return;
    setFetching(true);
    try {
      const { data, error } = await supabase
        .from('student_portfolios')
        .select('*')
        .eq('attendance_id', attendanceRecord.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFormData({
          notes: data.notes || '',
          homework: data.homework || '',
          behavior_notes: data.behavior_notes || '',
          score: data.score || 3,
          homework_status: data.homework_status || 'No Homework',
        });
        setCurrentPortfolio(data);
      } else {
        setFormData(EMPTY_FORM);
        setCurrentPortfolio(null);
      }
    } catch (e) {
      console.error('Portfolio fetch error:', e);
      toast.error('Could not load portfolio data');
    } finally {
      setFetching(false);
    }
  }, [attendanceRecord?.id]);

  // Fetch whenever the modal opens
  useEffect(() => {
    if (isOpen && attendanceRecord?.id) {
      fetchPortfolio();
    }
    // Refresh clipboard state whenever modal opens
    try {
      const stored = localStorage.getItem(CLIPBOARD_KEY);
      setClipboardData(stored ? JSON.parse(stored) : null);
    } catch {
      setClipboardData(null);
    }
  }, [isOpen, attendanceRecord?.id]);

  const handleCopy = () => {
    const payload = { notes: formData.notes, homework: formData.homework };
    localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload));
    setClipboardData(payload);
    toast.success('Lesson Notes & Next Tasks copied to clipboard');
  };

  const handlePaste = () => {
    if (!clipboardData) return;
    setFormData(prev => ({ ...prev, notes: clipboardData.notes, homework: clipboardData.homework }));
    toast.success('Pasted!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const upsertPayload = {
        ...formData,
        attendance_id: attendanceRecord.id,
        student_id: attendanceRecord.student_id,
        subject_id: attendanceRecord.subject_id,
      };
      if (currentPortfolio?.id) upsertPayload.id = currentPortfolio.id;

      const { error } = await supabase
        .from('student_portfolios')
        .upsert([upsertPayload], { onConflict: 'attendance_id' });

      if (error) throw error;
      toast.success('Portfolio updated!');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Lesson Portfolio" className="max-w-xl">
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Score */}
        <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-2xl flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-bold text-primary-600 tracking-wide">Session Performance</p>
              {/* Refresh button */}
              <button
                type="button"
                onClick={fetchPortfolio}
                disabled={fetching}
                title="Refresh data from server"
                className="p-1 rounded-lg text-primary-400 hover:text-primary-700 hover:bg-primary-100 transition-colors"
              >
                <SafeIcon icon={LuRefreshCw} size={13} className={fetching ? 'animate-spin' : ''} />
              </button>
              {fetching && (
                <span className="text-[9px] text-primary-400 font-medium">Loading...</span>
              )}
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFormData({ ...formData, score: s })}
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center transition-all',
                    formData.score >= s ? 'bg-primary-600 text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-400'
                  )}
                >
                  <SafeIcon icon={LuStar} size={18} />
                </button>
              ))}
            </div>
          </div>
          <p className="text-2xl font-black text-primary-700">
            {formData.score === 5 ? '🚀' : formData.score >= 4 ? '⭐️' : formData.score >= 3 ? '✅' : '⚠️'}
          </p>
        </div>

        <div className="space-y-4">
          {/* Copy / Paste toolbar */}
          <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 rounded-xl px-3 py-2 border border-slate-100 dark:border-slate-700">
            <span className="text-[10px] font-bold text-slate-400">Lesson Notes &amp; Next Tasks</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-[10px] font-bold text-slate-600 dark:text-slate-300 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-all"
              >
                <SafeIcon icon={LuCopy} size={11} />
                Copy
              </button>
              <button
                type="button"
                onClick={handlePaste}
                disabled={!clipboardData}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all border',
                  clipboardData
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                    : 'bg-slate-100 border-slate-200 text-slate-300 cursor-not-allowed'
                )}
              >
                <SafeIcon icon={LuClipboard} size={11} />
                Paste
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-full">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                <SafeIcon icon={LuClipboardList} size={16} className="text-amber-500" /> Previously Given Homework Status
              </label>
              <select
                className="w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3 text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                value={formData.homework_status}
                onChange={(e) => setFormData({ ...formData, homework_status: e.target.value })}
              >
                {HOMEWORK_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>

            <div className="col-span-full">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                <SafeIcon icon={LuMessageSquare} size={16} className="text-primary-500" /> Lesson Notes
              </label>
              <textarea
                className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 text-sm focus:ring-2 focus:ring-primary-500 outline-none min-h-[80px]"
                placeholder="What did we cover today?"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                <SafeIcon icon={LuClipboardList} size={16} className="text-amber-500" /> Next Tasks
              </label>
              <textarea
                className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 text-sm focus:ring-2 focus:ring-primary-500 outline-none min-h-[80px]"
                placeholder="Homework instructions..."
                value={formData.homework}
                onChange={(e) => setFormData({ ...formData, homework: e.target.value })}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                <SafeIcon icon={LuSmile} size={16} className="text-emerald-500" /> Behavior
              </label>
              <textarea
                className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 text-sm focus:ring-2 focus:ring-primary-500 outline-none min-h-[80px]"
                placeholder="Focus and participation..."
                value={formData.behavior_notes}
                onChange={(e) => setFormData({ ...formData, behavior_notes: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="ghost" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="flex-1" disabled={saving || fetching}>
            <SafeIcon icon={saving ? LuRefreshCw : LuSave} className={saving ? 'animate-spin mr-2' : 'mr-2'} size={18} />
            {saving ? 'Saving...' : 'Update Portfolio'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}